import { Box, Button, Grid, MenuItem, TextField } from '@mui/material';
import React, { FormEvent, useEffect, useState } from 'react';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import useAuthOktaUserFactorQuestions from '../../hooks/okta/useAuthOktaUserFactorQuestions';
import { useTranslation } from 'react-i18next';
import useAuthOktaFactorQuestionMutation from './hooks/useAuthOktaFactorQuestionMutation';
import langAuthOktaFactorQuestion from './langs/langAuthOktaFactorQuestion';

type FAuthOktaFactorQuestionProps = {
	onSave: () => void;
};

const FAuthOktaFactorQuestion = (props: FAuthOktaFactorQuestionProps) => {
	const { data, isLoading } = useAuthOktaUserFactorQuestions();

	const { t, i18n } = useTranslation();

	const [questionKeys, setQuestionKeys] = useState<string[] | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (data) {
			setQuestionKeys(data.map((x) => x.question));
		}
	}, [data]);

	const [questionKey, setQuestionKey] = useState<string | null>(null);
	const [answer, setAnswer] = useState<string | null>(null);

	const [error, setError] = useState<{ questionKey: string | null; answer: string | null }>({
		questionKey: null,
		answer: null,
	});

	const {
		mutateSetFactor: { mutate: setFactor },
	} = useAuthOktaFactorQuestionMutation();

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsSubmitting(true);

		if (!questionKey || !answer || answer.length < 4) {
			if (!questionKey) {
				setError((prevError) => ({
					...prevError,
					questionKey: t(langAuthOktaFactorQuestion.error_question_key_required.tag),
				}));
			}
			if (!answer) {
				setError((prevError) => ({
					...prevError,
					answer: t(langAuthOktaFactorQuestion.error_answer_required.tag),
				}));
			} else if (answer.length < 4) {
				setError((prevError) => ({
					...prevError,
					answer: t(langAuthOktaFactorQuestion.error_answer_too_short.tag),
				}));
			}
			setIsSubmitting(false);
		} else {
			setFactor(
				{ question: questionKey, answer },
				{
					onSuccess: () => {
						props.onSave();
					},
					onError: () => {
						setError((prevError) => ({
							...prevError,
							answer: t(langAuthOktaFactorQuestion.error_api.tag),
						}));
					},
					onSettled: () => {
						setIsSubmitting(false);
					},
				},
			);
		}
	};

	return (
		<>
			{!isLoading ? (
				<>
					<form onSubmit={handleSubmit}>
						<Grid container spacing={3}>
							{questionKeys ? (
								<Grid item xs={12}>
									<TextField
										variant="outlined"
										fullWidth
										select
										value={questionKey ?? null}
										label={t(langAuthOktaFactorQuestion.field_label_question_key.tag)}
										onChange={(e) => {
											setQuestionKey(e.target.value);
											setError((prevError) => ({ ...prevError, questionKey: null }));
										}}
										error={error && !!error.questionKey}
										helperText={error && error.questionKey ? error.questionKey : null}
									>
										{questionKeys
											.filter((questionKey) =>
												i18n.exists(
													`auth.okta_factor_question.question_option_label_${questionKey}`,
												),
											)
											.map((questionKey) => {
												return (
													<MenuItem key={questionKey} value={questionKey}>
														{t(
															`auth.okta_factor_question.question_option_label_${questionKey}`,
														)}
													</MenuItem>
												);
											})}
									</TextField>
								</Grid>
							) : (
								''
							)}
							<Grid item xs={12}>
								<TextField
									variant="outlined"
									size="medium"
									label={t(langAuthOktaFactorQuestion.field_label_answer.tag)}
									fullWidth
									autoComplete="off"
									type="text"
									value={answer ?? ''}
									error={error && !!error.answer}
									helperText={error && error.answer ? error.answer : null}
									onChange={(e) => {
										setAnswer(e.target.value);
										setError((prevError) => ({ ...prevError, answer: null }));
									}}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box style={{ textAlign: 'center' }}>
									<Button
										type="submit"
										disabled={isSubmitting}
										variant="contained"
										color="secondary"
										sx={{ minWidth: 160, fontWeight: 600 }}
										endIcon={<AppCustomIcon tag="arrow-right" />}
									>
										{t(langAuthOktaFactorQuestion.btn_save.tag)}
									</Button>
								</Box>
							</Grid>
						</Grid>
					</form>
				</>
			) : (
				''
			)}
		</>
	);
};

export default FAuthOktaFactorQuestion;
